import { useEffect, useMemo, useRef, useState } from "react";
import { X, Trash2, Archive, ArchiveRestore, Plus, Pencil, History, CornerDownLeft, CornerUpRight, Link2, UserPlus, Video } from "lucide-react";
import { Avatar } from "./Avatar";
import Vedlegg from "./Vedlegg";
import { STATUSER, PRIORITETER, medlem, fmtTid, renderRik, datoOm } from "./sakerUtils";

const Felt = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-[10.5px] font-semibold uppercase tracking-wider text-neutral-400">{label}</span>
    {children}
  </div>
);

const selectKl =
  "w-full text-[13px] border border-[#E7E4DB] rounded-lg px-2.5 py-1.5 bg-white text-neutral-800 focus:outline-none focus:border-neutral-400 transition-colors";

const DeloppgaveRad = ({ del, members, onToggle, onSett, onFjern, onTilSak }) => (
  <div className="group flex items-center gap-2.5 py-1.5" data-testid={`sak-deloppgave-${del.id}`}>
    <button
      type="button"
      onClick={onToggle}
      className={`w-[17px] h-[17px] rounded-[5px] border shrink-0 flex items-center justify-center transition-colors ${
        del.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-neutral-300 hover:border-neutral-500"
      }`}
      data-testid={`sak-deloppgave-toggle-${del.id}`}
    >
      {del.done && (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M1.5 5.5L4 8L8.5 2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
    <span className={`flex-1 text-[13px] leading-snug ${del.done ? "line-through text-neutral-400" : "text-neutral-800"}`}>
      {del.text}
    </span>
    <input
      type="date"
      value={del.due || ""}
      onChange={(e) => onSett({ due: e.target.value || null })}
      className="text-[11px] text-neutral-500 border border-transparent hover:border-[#E7E4DB] rounded px-1 py-0.5 bg-transparent w-[110px] opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
    />
    <select
      value={del.assigneeId || ""}
      onChange={(e) => onSett({ assigneeId: e.target.value || null })}
      className="text-[11px] text-neutral-500 border border-transparent hover:border-[#E7E4DB] rounded px-1 py-0.5 bg-transparent max-w-[110px] opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
      title="Ansvarlig for punktet"
    >
      <option value="">Ingen</option>
      {members.map((m) => (
        <option key={m.id} value={m.id}>{m.name}</option>
      ))}
    </select>
    {del.assigneeId && <Avatar person={medlem(members, del.assigneeId)} size={18} />}
    <button
      type="button"
      onClick={onTilSak}
      className="text-neutral-300 hover:text-neutral-700 opacity-0 group-hover:opacity-100 transition-all"
      title="Gjør om til egen sak"
      data-testid={`sak-deloppgave-promoter-${del.id}`}
    >
      <CornerUpRight size={13} />
    </button>
    <button
      type="button"
      onClick={onFjern}
      className="text-neutral-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
      data-testid={`sak-deloppgave-fjern-${del.id}`}
    >
      <X size={13} />
    </button>
  </div>
);

export default function SakDrawer({ sak, members, today, aktor, onClose, onUpdate, onComment, onFollowers, onDelete, onReplaceTask, onPromote, onMote }) {
  const [tittel, setTittel] = useState(sak.title);
  const [redigerBesk, setRedigerBesk] = useState(false);
  const [besk, setBesk] = useState(sak.description || "");
  const [nyDel, setNyDel] = useState("");
  const [nyEtikett, setNyEtikett] = useState("");
  const [kommentar, setKommentar] = useState("");
  const [sender, setSender] = useState(false);
  const [visAllAktivitet, setVisAllAktivitet] = useState(false);
  const [visFolgere, setVisFolgere] = useState(false);
  const [mentions, setMentions] = useState([]);
  const [mentionQ, setMentionQ] = useState(null);
  const beskRef = useRef(null);
  const kommentarRef = useRef(null);

  useEffect(() => { setTittel(sak.title); }, [sak.id, sak.title]);
  useEffect(() => { if (!redigerBesk) setBesk(sak.description || ""); }, [sak.id, sak.description, redigerBesk]);
  useEffect(() => {
    const esc = (e) => {
      if (e.key !== "Escape") return;
      if (visFolgere) { setVisFolgere(false); return; }
      onClose();
    };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose, visFolgere]);

  const delFerdig = (sak.subtasks || []).filter((s) => s.done).length;
  const delTotal = (sak.subtasks || []).length;
  const aktivitet = useMemo(() => [...(sak.activity || [])].reverse(), [sak.activity]);
  const visteAktiviteter = visAllAktivitet ? aktivitet : aktivitet.slice(0, 5);

  const lagreTittel = () => {
    const t = tittel.trim();
    if (t && t !== sak.title) onUpdate({ title: t });
    else setTittel(sak.title);
  };

  const settDeloppgaver = (subtasks) => onUpdate({ subtasks });

  const leggTilDel = () => {
    const t = nyDel.trim();
    if (!t) return;
    settDeloppgaver([...(sak.subtasks || []), { id: crypto.randomUUID(), text: t, done: false, assigneeId: null, due: null }]);
    setNyDel("");
  };

  const leggTilEtikett = () => {
    const t = nyEtikett.trim();
    if (!t || (sak.labels || []).includes(t)) { setNyEtikett(""); return; }
    onUpdate({ labels: [...(sak.labels || []), t] });
    setNyEtikett("");
  };

  const sendKommentar = async () => {
    const t = kommentar.trim();
    if (!t || sender) return;
    setSender(true);
    try {
      const aktive = mentions.filter((id) => {
        const m = medlem(members, id);
        return m && t.includes(`@${m.name}`);
      });
      await onComment(t, aktive);
      setKommentar("");
      setMentions([]);
      setMentionQ(null);
    } finally {
      setSender(false);
    }
  };

  const toggleFolger = (mid) => {
    const f = sak.followers || [];
    onFollowers(f.includes(mid) ? f.filter((x) => x !== mid) : [...f, mid]);
  };

  const vedKommentarEndring = (e) => {
    const v = e.target.value;
    setKommentar(v);
    const foran = v.slice(0, e.target.selectionStart ?? v.length);
    const m = foran.match(/(^|\s)@([\wæøåÆØÅ.-]*)$/);
    setMentionQ(m ? m[2].toLowerCase() : null);
  };

  const mentionForslag = mentionQ === null
    ? []
    : members.filter((m) => m.name.toLowerCase().includes(mentionQ)).slice(0, 5);

  const velgMention = (m) => {
    setKommentar((v) => v.replace(/@([\wæøåÆØÅ.-]*)$/, `@${m.name} `));
    setMentions((prev) => (prev.includes(m.id) ? prev : [...prev, m.id]));
    setMentionQ(null);
    setTimeout(() => kommentarRef.current?.focus(), 10);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="sak-drawer">
      <div className="sak-backdrop absolute inset-0 bg-black/25" onClick={onClose} data-testid="sak-drawer-backdrop" />
      <aside className="sak-drawer relative w-full max-w-[620px] h-full bg-white shadow-2xl border-l border-[#E7E4DB] flex flex-col">
        {/* Toppbar */}
        <div className="flex items-center gap-2 px-3 sm:px-5 py-3 border-b border-neutral-100 shrink-0">
          <div className="flex bg-neutral-100 rounded-lg p-0.5 overflow-x-auto sak-noscroll max-w-full">
            {STATUSER.map((s) => (
              <button
                key={s.k}
                type="button"
                onClick={() => sak.status !== s.k && onUpdate({ status: s.k })}
                className={`flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 py-1 rounded-md transition-all whitespace-nowrap ${
                  sak.status === s.k ? "bg-white shadow-sm text-neutral-900" : "text-neutral-500 hover:text-neutral-800"
                }`}
                data-testid={`sak-status-${s.k}`}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
                {s.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={() => onUpdate({ archived: !sak.archived })}
              className="p-1.5 text-neutral-400 hover:text-neutral-800 hover:bg-neutral-100 rounded-md transition-colors"
              title={sak.archived ? "Gjenopprett" : "Arkiver"}
              data-testid="sak-arkiver-btn"
            >
              {sak.archived ? <ArchiveRestore size={15} /> : <Archive size={15} />}
            </button>
            <button
              type="button"
              onClick={onMote}
              className="p-1.5 text-neutral-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
              title="Opprett møte om denne saken — Outlook/Teams"
              data-testid="sak-mote-btn"
            >
              <Video size={15} />
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
              title="Slett saken"
              data-testid="sak-slett-btn"
            >
              <Trash2 size={15} />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 text-neutral-400 hover:text-neutral-800 hover:bg-neutral-100 rounded-md transition-colors"
              data-testid="sak-drawer-lukk"
            >
              <X size={17} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-4 sm:px-6 pt-5 pb-6">
            {sak.archived && (
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 rounded px-2 py-1 inline-block">
                Arkivert
              </p>
            )}
            <textarea
              value={tittel}
              onChange={(e) => setTittel(e.target.value)}
              onBlur={lagreTittel}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
              rows={Math.min(3, Math.ceil(tittel.length / 44) || 1)}
              maxLength={300}
              className="w-full resize-none bg-transparent text-[21px] font-semibold leading-tight text-neutral-900 focus:outline-none placeholder:text-neutral-300"
              placeholder="Tittel"
              data-testid="sak-drawer-tittel"
            />

            {/* Metafelter */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-4 mt-4">
              <Felt label="Ansvarlig">
                <div className="flex items-center gap-2">
                  {sak.assigneeId && <Avatar person={medlem(members, sak.assigneeId)} size={24} />}
                  <select
                    value={sak.assigneeId || ""}
                    onChange={(e) => onUpdate({ assigneeId: e.target.value || null })}
                    className={selectKl}
                    data-testid="sak-ansvarlig-select"
                  >
                    <option value="">Ingen</option>
                    {members.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
              </Felt>
              <Felt label="Frist">
                <input
                  type="date"
                  value={sak.dueDate || ""}
                  onChange={(e) => onUpdate({ dueDate: e.target.value || null })}
                  className={selectKl}
                  data-testid="sak-frist-input"
                />
                <div className="flex items-center gap-1 flex-wrap">
                  {[{ l: "I dag", d: 0 }, { l: "I morgen", d: 1 }, { l: "+1 uke", d: 7 }].map((h) => {
                    const dato = datoOm(h.d);
                    const aktiv = sak.dueDate === dato;
                    return (
                      <button
                        key={h.l}
                        type="button"
                        onClick={() => onUpdate({ dueDate: aktiv ? null : dato })}
                        className={`text-[10px] font-medium rounded-full px-2 py-0.5 border transition-colors ${
                          aktiv ? "border-neutral-900 bg-neutral-900 text-white" : "border-[#E7E4DB] text-neutral-400 hover:text-neutral-700 hover:border-neutral-400"
                        }`}
                        data-testid={`sak-frist-hurtig-${h.d}`}
                      >
                        {h.l}
                      </button>
                    );
                  })}
                </div>
              </Felt>
              <Felt label="Prioritet">
                <select
                  value={sak.priority || 2}
                  onChange={(e) => onUpdate({ priority: Number(e.target.value) })}
                  className={selectKl}
                  data-testid="sak-prioritet-select"
                >
                  {PRIORITETER.map((p) => (
                    <option key={p.v} value={p.v}>{p.label}</option>
                  ))}
                </select>
              </Felt>
            </div>

            {/* Følgere */}
            <div className="mt-4 flex items-center gap-2 flex-wrap relative" data-testid="sak-folgere">
              <span className="text-[10.5px] font-semibold uppercase tracking-wider text-neutral-400">Følgere</span>
              {(sak.followers || []).length > 0 && (
                <div className="flex items-center -space-x-1.5">
                  {(sak.followers || []).map((fid) => (
                    <span key={fid} title={medlem(members, fid)?.name} className="ring-2 ring-white rounded-full">
                      <Avatar person={medlem(members, fid)} size={22} />
                    </span>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => setVisFolgere((v) => !v)}
                className="w-[22px] h-[22px] rounded-full border border-dashed border-neutral-300 text-neutral-400 hover:text-neutral-700 hover:border-neutral-500 flex items-center justify-center transition-colors"
                title="Administrer følgere — de får e-post ved kommentarer og statusendringer"
                data-testid="sak-folger-add"
              >
                <UserPlus size={11} />
              </button>
              {visFolgere && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setVisFolgere(false)} />
                  <div className="absolute top-7 left-0 z-20 w-[250px] bg-white border border-[#E7E4DB] rounded-xl shadow-xl p-1.5" data-testid="sak-folger-meny">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 px-2 pt-1 pb-1.5">Følgere varsles på e-post</p>
                    {members.map((m) => {
                      const valgt = (sak.followers || []).includes(m.id);
                      return (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => toggleFolger(m.id)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-neutral-50 text-left transition-colors"
                          data-testid={`sak-folger-opt-${m.id}`}
                        >
                          <Avatar person={m} size={20} />
                          <span className="flex-1 text-[12.5px] text-neutral-700 truncate">{m.name}</span>
                          <span className={`w-[15px] h-[15px] rounded-[4px] border flex items-center justify-center transition-colors ${valgt ? "bg-neutral-900 border-neutral-900 text-white" : "border-neutral-300"}`}>
                            {valgt && (
                              <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                                <path d="M1.5 5.5L4 8L8.5 2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </span>
                        </button>
                      );
                    })}
                    {members.length === 0 && <p className="text-[12px] text-neutral-400 px-2 py-1.5">Ingen personer registrert</p>}
                  </div>
                </>
              )}
            </div>

            {/* Etiketter */}
            <div className="mt-4 flex items-center gap-1.5 flex-wrap">
              {(sak.labels || []).map((l) => (
                <span key={l} className="group inline-flex items-center gap-1 text-[11px] font-medium text-neutral-600 border border-[#E7E4DB] rounded-full pl-2.5 pr-1.5 py-0.5">
                  {l}
                  <button
                    type="button"
                    onClick={() => onUpdate({ labels: (sak.labels || []).filter((x) => x !== l) })}
                    className="text-neutral-300 hover:text-red-500 transition-colors"
                  >
                    <X size={11} />
                  </button>
                </span>
              ))}
              {(sak.labels || []).length < 8 && (
                <input
                  value={nyEtikett}
                  onChange={(e) => setNyEtikett(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); leggTilEtikett(); } }}
                  onBlur={leggTilEtikett}
                  placeholder="+ Etikett"
                  maxLength={30}
                  className="text-[11px] text-neutral-500 bg-transparent border border-dashed border-[#E7E4DB] rounded-full px-2.5 py-0.5 w-[86px] focus:w-[130px] focus:outline-none focus:border-neutral-400 transition-all placeholder:text-neutral-400"
                  data-testid="sak-etikett-input"
                />
              )}
            </div>

            {/* Koblede objekter (Event OS) */}
            {(sak.linkedEntities || []).length > 0 && (
              <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="sak-koblinger">
                {sak.linkedEntities.map((le) => (
                  <span
                    key={`${le.type}-${le.id}`}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-700 bg-indigo-50 rounded-full pl-2.5 pr-1.5 py-0.5"
                  >
                    <Link2 size={10} /> {le.label || le.type}
                    <button
                      type="button"
                      onClick={() =>
                        onUpdate({
                          linkedEntities: (sak.linkedEntities || []).filter((x) => !(x.type === le.type && x.id === le.id)),
                        })
                      }
                      className="text-indigo-300 hover:text-red-500 transition-colors"
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Beskrivelse */}
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10.5px] font-semibold uppercase tracking-wider text-neutral-400">Beskrivelse</span>
                {!redigerBesk && (
                  <button
                    type="button"
                    onClick={() => { setRedigerBesk(true); setTimeout(() => beskRef.current?.focus(), 30); }}
                    className="text-neutral-300 hover:text-neutral-700 transition-colors"
                    data-testid="sak-beskrivelse-rediger"
                  >
                    <Pencil size={12} />
                  </button>
                )}
              </div>
              {redigerBesk ? (
                <div>
                  <textarea
                    ref={beskRef}
                    value={besk}
                    onChange={(e) => setBesk(e.target.value)}
                    rows={6}
                    maxLength={8000}
                    className="w-full text-[13.5px] leading-relaxed border border-[#E7E4DB] rounded-lg px-3 py-2.5 focus:outline-none focus:border-neutral-400 transition-colors"
                    placeholder="Skriv en beskrivelse … (Markdown støttes)"
                    data-testid="sak-beskrivelse-input"
                  />
                  <div className="flex items-center gap-2 mt-1.5">
                    <button
                      type="button"
                      onClick={() => { onUpdate({ description: besk }); setRedigerBesk(false); }}
                      className="text-[12px] font-medium bg-neutral-900 text-white px-3 py-1.5 rounded-lg hover:bg-neutral-700 transition-colors"
                      data-testid="sak-beskrivelse-lagre"
                    >
                      Lagre
                    </button>
                    <button
                      type="button"
                      onClick={() => { setBesk(sak.description || ""); setRedigerBesk(false); }}
                      className="text-[12px] text-neutral-500 px-2 py-1.5 hover:text-neutral-800 transition-colors"
                    >
                      Avbryt
                    </button>
                    <span className="ml-auto text-[10.5px] text-neutral-300">Markdown støttes</span>
                  </div>
                </div>
              ) : sak.description ? (
                <div
                  className="rik cursor-text"
                  onClick={() => { setRedigerBesk(true); setTimeout(() => beskRef.current?.focus(), 30); }}
                  dangerouslySetInnerHTML={{ __html: renderRik(sak.description) }}
                  data-testid="sak-beskrivelse"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => { setRedigerBesk(true); setTimeout(() => beskRef.current?.focus(), 30); }}
                  className="text-[13px] text-neutral-400 hover:text-neutral-600 transition-colors"
                  data-testid="sak-beskrivelse-tom"
                >
                  Legg til en beskrivelse …
                </button>
              )}
            </div>

            {/* Deloppgaver */}
            <div className="mt-7">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10.5px] font-semibold uppercase tracking-wider text-neutral-400">Deloppgaver</span>
                {delTotal > 0 && (
                  <span className={`text-[11px] tabular-nums font-medium ${delFerdig === delTotal ? "text-emerald-600" : "text-neutral-400"}`}>
                    {delFerdig}/{delTotal}
                  </span>
                )}
              </div>
              {delTotal > 0 && (
                <div className="h-1 rounded-full bg-neutral-100 overflow-hidden mb-2">
                  <div
                    className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                    style={{ width: `${Math.round((delFerdig / delTotal) * 100)}%` }}
                  />
                </div>
              )}
              <div className="divide-y divide-neutral-50">
                {(sak.subtasks || []).map((del) => (
                  <DeloppgaveRad
                    key={del.id}
                    del={del}
                    members={members}
                    onToggle={() => settDeloppgaver((sak.subtasks || []).map((s) => (s.id === del.id ? { ...s, done: !s.done } : s)))}
                    onSett={(endr) => settDeloppgaver((sak.subtasks || []).map((s) => (s.id === del.id ? { ...s, ...endr } : s)))}
                    onFjern={() => settDeloppgaver((sak.subtasks || []).filter((s) => s.id !== del.id))}
                    onTilSak={() => onPromote(del)}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <Plus size={14} className="text-neutral-300 shrink-0" />
                <input
                  value={nyDel}
                  onChange={(e) => setNyDel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); leggTilDel(); } }}
                  placeholder="Legg til deloppgave — Enter for å lagre"
                  maxLength={300}
                  className="flex-1 text-[13px] py-1 bg-transparent text-neutral-900 focus:outline-none placeholder:text-neutral-300"
                  data-testid="sak-deloppgave-input"
                />
              </div>
            </div>

            {/* Vedlegg */}
            <Vedlegg sak={sak} aktor={aktor} onReplaceTask={onReplaceTask} />

            {/* Kommentarer */}
            <div className="mt-8">
              <span className="text-[10.5px] font-semibold uppercase tracking-wider text-neutral-400">
                Kommentarer {(sak.comments || []).length > 0 && `· ${sak.comments.length}`}
              </span>
              <div className="mt-3 space-y-4">
                {(sak.comments || []).map((k) => {
                  const forfatter = members.find((m) => m.name === k.author);
                  return (
                    <div key={k.id} className="flex gap-2.5" data-testid={`sak-kommentar-${k.id}`}>
                      {forfatter ? (
                        <span className="shrink-0 mt-0.5"><Avatar person={forfatter} size={24} /></span>
                      ) : (
                        <span className="w-6 h-6 rounded-full bg-neutral-200 text-neutral-600 text-[9.5px] font-semibold flex items-center justify-center shrink-0 mt-0.5">
                          {String(k.author || "?").trim().split(/\s+/).slice(0, 2).map((d) => d[0]).join("").toUpperCase()}
                        </span>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-[11.5px] text-neutral-500">
                          <span className="font-semibold text-neutral-800">{k.author}</span>
                          <span className="ml-2 text-neutral-400">{fmtTid(k.at)}</span>
                        </p>
                        <div className="rik mt-0.5" dangerouslySetInnerHTML={{ __html: renderRik(k.text, members.map((m) => m.name)) }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 border border-[#E7E4DB] rounded-xl focus-within:border-neutral-400 transition-colors relative">
                {mentionForslag.length > 0 && (
                  <div className="absolute bottom-full left-2 mb-1 w-[240px] bg-white border border-[#E7E4DB] rounded-xl shadow-xl p-1.5 z-20" data-testid="sak-mention-liste">
                    {mentionForslag.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => velgMention(m)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-neutral-50 text-left transition-colors"
                        data-testid={`sak-mention-opt-${m.id}`}
                      >
                        <Avatar person={m} size={20} />
                        <span className="flex-1 text-[12.5px] text-neutral-700 truncate">{m.name}</span>
                        {m.email && <span className="text-[10px] text-neutral-300">e-post</span>}
                      </button>
                    ))}
                  </div>
                )}
                <textarea
                  ref={kommentarRef}
                  value={kommentar}
                  onChange={vedKommentarEndring}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendKommentar(); }
                    if (e.key === "Escape" && mentionQ !== null) { e.stopPropagation(); setMentionQ(null); }
                  }}
                  rows={2}
                  maxLength={2000}
                  placeholder={`Kommenter som ${aktor || "Admin"} — @ for å tagge …`}
                  className="w-full text-[13px] px-3 pt-2.5 pb-1 rounded-t-xl focus:outline-none resize-none placeholder:text-neutral-300"
                  data-testid="sak-kommentar-input"
                />
                <div className="flex items-center justify-between px-3 pb-2">
                  <span className="text-[10px] text-neutral-300 inline-flex items-center gap-1">
                    <CornerDownLeft size={10} /> Cmd/Ctrl + Enter · @navn varsles på e-post
                  </span>
                  <button
                    type="button"
                    onClick={sendKommentar}
                    disabled={!kommentar.trim() || sender}
                    className="text-[12px] font-medium bg-neutral-900 text-white px-3 py-1 rounded-lg hover:bg-neutral-700 disabled:opacity-30 transition-all"
                    data-testid="sak-kommentar-send"
                  >
                    {sender ? "Sender …" : "Kommenter"}
                  </button>
                </div>
              </div>
            </div>

            {/* Aktivitet */}
            <div className="mt-8 pb-2">
              <span className="text-[10.5px] font-semibold uppercase tracking-wider text-neutral-400 inline-flex items-center gap-1.5">
                <History size={11} /> Aktivitet
              </span>
              <div className="mt-2.5 space-y-1.5" data-testid="sak-aktivitet">
                {visteAktiviteter.map((a, i) => (
                  <p key={`${a.at}-${i}`} className="text-[11.5px] text-neutral-400 leading-relaxed">
                    <span className="text-neutral-600 font-medium">{a.actor}</span> · {a.text}
                    <span className="ml-1.5 text-neutral-300">{fmtTid(a.at)}</span>
                  </p>
                ))}
              </div>
              {aktivitet.length > 5 && (
                <button
                  type="button"
                  onClick={() => setVisAllAktivitet((v) => !v)}
                  className="mt-2 text-[11px] text-neutral-400 hover:text-neutral-700 underline underline-offset-2 transition-colors"
                  data-testid="sak-aktivitet-visalle"
                >
                  {visAllAktivitet ? "Vis færre" : `Vis alle (${aktivitet.length})`}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="shrink-0 px-6 py-2.5 border-t border-neutral-100 flex items-center justify-between text-[10.5px] text-neutral-300">
          <span>Opprettet {fmtTid(sak.createdAt)}</span>
          <span>Sist endret {fmtTid(sak.updatedAt)}</span>
        </div>
      </aside>
    </div>
  );
}
